import { borderRadius, breakpoints, color, spacing } from '@/theme/tokens';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import IconButtonWhite from '@/components/formElements/iconButtons/IconButtonWhite';
import IconWarningFilledColor from '@/assets/icons/IconWarningFilledColor';
import IconInfoFilled from '@/assets/icons/IconInfoFilled';
import IconCheckCircleFilled from '@/assets/icons/IconCheckCircleFilled';
import IconClose from '@/assets/icons/IconClose';
import IconErrorFilled from '@/assets/icons/IconErrorFilled';

type AlertBannerProps = {
    title?: string;
    text?: string | React.ReactNode;
    sideActions?: React.ReactNode;
    bottomActions?: React.ReactNode;
    type: 'warning' | 'info' | 'success' | 'error';
    style?: React.CSSProperties;
    showCloseButton?: boolean;
    onClose?: () => void;
};

const Wrapper = styled.div<{ $type: AlertBannerProps['type']; $showCloseButton: AlertBannerProps['showCloseButton'] }>`
    position: relative;
    border-radius: ${borderRadius[8]};
    border: 1px solid ${color.black[20]};
    padding: ${spacing[16]} ${spacing[16]} ${spacing[16]} ${spacing[12]};
    ${({ $showCloseButton }) => $showCloseButton && `padding-right: 76px;`}

    ${({ $type }) => $type === 'warning' && `background-color: ${color.gray[80]};`}
    ${({ $type }) => $type === 'info' && `background-color: ${color.gray[80]};`}
    ${({ $type }) => $type === 'success' && `background-color: ${color.gray[80]};`}
    ${({ $type }) => $type === 'error' && `background-color: ${color.brand[50]};`}

    @media (max-width: ${breakpoints.md}) {
        padding-right: ${spacing[16]} !important;
    }
`;

const Content = styled.div<{ $showCloseButton: AlertBannerProps['showCloseButton'] }>`
    display: flex;
    flex-direction: column;
`;

const WrapperContent = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: ${spacing[32]};

    @media (max-width: ${breakpoints.md}) {
        flex-direction: column;
        gap: ${spacing[16]};
        align-items: start;
    }
`;

const SideActions = styled.div`
    display: flex;
    flex-direction: row;
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    gap: ${spacing[16]};

    @media (max-width: ${breakpoints.md}) {
        padding-left: 38px;
    }
`;

const BottomActions = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${spacing[16]};
`;

const TitleWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: start;
    gap: ${spacing[10]};
    margin-bottom: ${spacing[4]};
`;

const TitleWrapperInner = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    min-height: 28px;

    @media (max-width: ${breakpoints.md}) {
        padding-right: 44px;
    }
`;

const IconWrapper = styled.div`
    height: 28px;
    width: 28px;
`;

const CloseButtonWrapper = styled.div`
    position: absolute;
    top: 10px;
    right: 10px;
`;

const AlertBanner = (props: AlertBannerProps) => {
    const { title, text, sideActions, bottomActions, type, style, showCloseButton, onClose } = props;

    return (
        <Wrapper $type={type} style={style} $showCloseButton={showCloseButton}>
            {showCloseButton && (
                <CloseButtonWrapper>
                    <IconButtonWhite
                        onClick={onClose}
                        sx={{
                            color: color.white[0],
                            width: 40,
                            height: 40,
                            '&:hover': {
                                backgroundColor: color.white[10],
                            },
                        }}
                    >
                        <IconClose color={color.white[0]} width={22} height={22} />
                    </IconButtonWhite>
                </CloseButtonWrapper>
            )}
            <WrapperContent>
                <Content $showCloseButton={showCloseButton}>
                    <TitleWrapper>
                        <IconWrapper>
                            {type === 'warning' && <IconWarningFilledColor width={28} height={28} />}
                            {type === 'info' && <IconInfoFilled width={28} height={28} />}
                            {type === 'success' && <IconCheckCircleFilled width={28} height={28} />}
                            {type === 'error' && <IconErrorFilled width={28} height={28} />}
                        </IconWrapper>
                        <TitleWrapperInner>
                            <Typography variant="label5semibold" color={color.white[0]}>
                                {title}
                            </Typography>
                        </TitleWrapperInner>
                    </TitleWrapper>
                    <Typography variant="paragraph4regular" color={color.white[90]} sx={{ paddingLeft: '38px' }}>
                        {text}
                    </Typography>
                    {bottomActions && <BottomActions>{bottomActions}</BottomActions>}
                </Content>
                {sideActions && <SideActions>{sideActions}</SideActions>}
            </WrapperContent>
        </Wrapper>
    );
};

export default AlertBanner;
